import { Form } from '@formily/core'
import { useCreation, useUpdateEffect } from 'ahooks'
import { forOwn, isNil } from 'lodash'

import { GenericalRecord } from '@eastcoal/common/types/utilities'

import { FormOptions, createForm } from '../utils/createForm'

export type CreationFormOptions<T extends GenericalRecord> = Pick<
  FormOptions<T>,
  | 'initialValues'
  | 'values'
  | 'pattern'
  | 'display'
  | 'effects'
  | 'validateFirst'
  | 'validateDisplay'
  | 'validatePattern'
  | 'mergeStrategy'
> & {
  form?: Form<T>
}

export const useCreationForm = <T extends GenericalRecord>(
  props: CreationFormOptions<T> = {}
) => {
  const { form, values, initialValues, pattern, display, effects } = props

  const finalForm = useCreation(() => {
    if (form) {
      forOwn(props, (value, key) => {
        if (!isNil(value)) {
          form[key] = value
        }
      })
      return form
    }
    return createForm<T>(props)
  }, [form])

  useUpdateEffect(() => {
    if (!isNil(initialValues)) {
      finalForm.setInitialValues(initialValues)
    }
  }, [initialValues])

  useUpdateEffect(() => {
    if (!isNil(values)) {
      finalForm.setValues(values)
    }
  }, [values])

  useUpdateEffect(() => {
    if (!isNil(pattern)) {
      finalForm.setPattern(pattern)
    }
  }, [pattern])

  useUpdateEffect(() => {
    if (!isNil(display)) {
      finalForm.setDisplay(display)
    }
  }, [display])

  useUpdateEffect(() => {
    if (!isNil(effects)) {
      finalForm.setEffects(effects)
    }
  }, [effects])

  return finalForm
}
